
import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRolePermissions } from "@/hooks/rollenbeheer/useRolePermissions";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import RollenBeheerHeader from "@/components/rollenbeheer/RollenBeheerHeader";
import RollenBeheerContent from "@/components/rollenbeheer/RollenBeheerContent";
import DatabaseBadge from "@/components/rollenbeheer/DatabaseBadge";

const Rollenbeheer = () => {
  const { 
    permissions, 
    isLoading, 
    isDirty,
    updatePermission,
    savePermissions,
    initialized,
    loadAttempts,
    databaseInitialized,
    lastSaveTime
  } = useRolePermissions();
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [saveCount, setSaveCount] = useState(0);
  
  // Debug log for component mount and permission state with timestamp
  useEffect(() => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [Rollenbeheer] Component mounted`);
    console.log(`[${timestamp}] [Rollenbeheer] Initial permissions count:`, permissions?.length || 0);
    console.log(`[${timestamp}] [Rollenbeheer] Is dirty:`, isDirty);
    console.log(`[${timestamp}] [Rollenbeheer] Is initialized:`, initialized);
    console.log(`[${timestamp}] [Rollenbeheer] Load attempts:`, loadAttempts);
    console.log(`[${timestamp}] [Rollenbeheer] Database initialized:`, databaseInitialized);
    console.log(`[${timestamp}] [Rollenbeheer] Last save time:`, lastSaveTime);
  }, [permissions, isDirty, initialized, loadAttempts, databaseInitialized, lastSaveTime]);
  
  // Debug log for permission changes
  useEffect(() => {
    console.log(`[${new Date().toISOString()}] [Rollenbeheer] Permissions updated, count:`, permissions?.length || 0);
    console.log(`[${new Date().toISOString()}] [Rollenbeheer] isDirty state:`, isDirty);
  }, [permissions, isDirty]);
  
  // Ensure only management has access
  useEffect(() => {
    console.log(`[${new Date().toISOString()}] [Rollenbeheer] Checking role:`, user?.role);
    if (user && user.role !== "management") {
      toast.error("Alleen management heeft toegang tot rollenbeheer");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Enhanced save function with better error handling and verification
  const handleSave = useCallback(async () => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [Rollenbeheer] Saving permissions, isDirty:`, isDirty);
    
    if (!isDirty) {
      toast.info("Geen wijzigingen om op te slaan");
      return;
    }
    
    setIsSaving(true);
    try {
      console.log(`[${timestamp}] [Rollenbeheer] Starting save operation`);
      const success = await savePermissions();
      
      if (success) {
        console.log(`[${timestamp}] [Rollenbeheer] Save successful, updating counter`);
        setSaveCount(prev => prev + 1);
        
        toast.success("Wijzigingen opgeslagen in de database");
      } else {
        console.error(`[${timestamp}] [Rollenbeheer] Save operation failed`);
        toast.error("Opslaan mislukt. Probeer het opnieuw of herlaad de pagina.");
      }
    } catch (error: any) {
      console.error(`[${timestamp}] [Rollenbeheer] Error during save:`, error);
      toast.error(`Fout bij opslaan: ${error?.message || "Onbekende fout"}`);
    } finally {
      setIsSaving(false);
    }
  }, [isDirty, savePermissions]);

  // Force reload with confirmation
  const handleRefresh = useCallback(() => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [Rollenbeheer] Manual refresh requested`);
    
    if (isDirty) {
      console.log(`[${timestamp}] [Rollenbeheer] Warning user about unsaved changes`);
      if (window.confirm("Er zijn niet-opgeslagen wijzigingen. Weet je zeker dat je de pagina wilt herladen? Alle wijzigingen gaan verloren.")) {
        console.log(`[${timestamp}] [Rollenbeheer] User confirmed refresh despite unsaved changes`);
        window.location.reload();
      }
    } else {
      console.log(`[${timestamp}] [Rollenbeheer] Refreshing page (no unsaved changes)`);
      window.location.reload();
    }
  }, [isDirty]);

  return (
    <div className="space-y-6">
      <RollenBeheerHeader 
        isDirty={isDirty}
        isLoading={isLoading}
        isSaving={isSaving}
        onRefresh={handleRefresh}
        onSave={handleSave}
      />
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <span>Beheer toegangsrechten per rol</span>
            <DatabaseBadge isInitialized={databaseInitialized} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RollenBeheerContent 
            isLoading={isLoading}
            permissions={permissions}
            onUpdatePermission={updatePermission}
            databaseInitialized={databaseInitialized}
            lastSaveTime={lastSaveTime}
            onRefresh={handleRefresh}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default Rollenbeheer;
